import type { InterfacePageEntity } from '@unifyapps/defs/types/page';
import type { ValidateEntityTypes } from '../../../../utils/entityValidation/validateEntityName';
import { getActiveInterfacePage } from '../state';

// Minimal interface store shape needed for getActiveInterfacePage - works for both runner and builder stores
type MinimalInterfaceStoreState = Parameters<typeof getActiveInterfacePage>[0];

/**
 * Parameters for building entity context for displayName uniqueness validation.
 */
export type GetUniqueDisplayNameContextParams = {
  /** The current interface page containing page-level entities */
  interfacePage: InterfacePageEntity;
  /** Page-level dataSources for validation */
  dataSources: ValidateEntityTypes['dataSources'];
  /** App-level dataSources for validation (merged with page dataSources) */
  appDataSources: ValidateEntityTypes['dataSources'];
  /**
   * Optional function to get app-level interface store state.
   * When undefined, app-level entities are not included in uniqueness checking,
   * which means names could potentially collide with app-level entity names.
   */
  getAppInterfaceStoreState?: () => MinimalInterfaceStoreState;
};

/**
 * Return type containing all merged entity collections for uniqueness validation.
 */
export type UniqueDisplayNameContext = {
  dataSources: ValidateEntityTypes['dataSources'];
  pageVariables: ValidateEntityTypes['pageVariables'];
  pageFunctions: ValidateEntityTypes['pageFunctions'];
  blocks: ValidateEntityTypes['blocks'];
  folders: ValidateEntityTypes['folders'];
  walkthroughSteps: ValidateEntityTypes['walkthroughSteps'];
};

/**
 * Builds a unified context of all entities for displayName uniqueness validation.
 *
 * This function merges page-level and app-level entities to ensure that entity names
 * are unique across the entire application scope. It collects:
 * - DataSources
 * - Page variables (both page and app level)
 * - Page functions (both page and app level)
 * - Blocks (both page and app level)
 * - Folders (both page and app level)
 * - Walkthrough steps (both page and app level)
 *
 * @param params - Parameters containing page context and optional app store getter
 * @returns A unified context object with all entities merged for uniqueness checking
 *
 * @remarks
 * When `getAppInterfaceStoreState` is not provided, app-level entities are empty.
 * This means entity names will only be validated against page-level entities,
 * potentially allowing collisions with app-level names.
 */
export function getUniqueDisplayNameContext({
  interfacePage,
  dataSources,
  appDataSources,
  getAppInterfaceStoreState,
}: GetUniqueDisplayNameContextParams): UniqueDisplayNameContext {
  // Get page-level entities
  const pageVariables = interfacePage.properties.pageVariables ?? {};
  const pageFunctions = interfacePage.properties.pageFunctions ?? {};
  const blocks = interfacePage.properties.blocks ?? {};
  const folders = interfacePage.properties.folders ?? {};
  const walkthroughSteps = interfacePage.properties.walkthrough?.steps ?? {};

  // Get app-level entities for uniqueness checking across entire app
  const appInterfaceStoreState = getAppInterfaceStoreState?.();
  const appInterfacePage = appInterfaceStoreState
    ? getActiveInterfacePage(appInterfaceStoreState)
    : undefined;

  const appPageVariables = appInterfacePage?.properties.pageVariables ?? {};
  const appPageFunctions = appInterfacePage?.properties.pageFunctions ?? {};
  const appBlocks = appInterfacePage?.properties.blocks ?? {};
  const appFolders = appInterfacePage?.properties.folders ?? {};
  const appWalkthroughSteps = appInterfacePage?.properties.walkthrough?.steps ?? {};

  // Merge page-level and app-level entities
  return {
    dataSources: { ...appDataSources, ...dataSources },
    pageVariables: { ...appPageVariables, ...pageVariables },
    pageFunctions: { ...appPageFunctions, ...pageFunctions },
    blocks: { ...appBlocks, ...blocks },
    folders: { ...appFolders, ...folders },
    walkthroughSteps: { ...appWalkthroughSteps, ...walkthroughSteps },
  };
}
